import React, { useCallback, useRef, useState } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { useIsMounted } from '../../hooks/useIsMounted';
import { useTheme } from '../../theme';
import type { MenuToggleButtonProps, ToggleMenu } from './MenuToggleButton';
import { MenuToggleButton } from './MenuToggleButton';
import { DropdownMenu, DropdownMenuContent } from './view';

interface MenuProps {
    toggleButtonContent: React.ReactNode;
    children: React.ReactNode;
    className?: string;
    isPortal?: boolean;
    isForcedOpen?: boolean;
    ToggleButton?: React.ComponentType<MenuToggleButtonProps>;
    contentProps?: React.ComponentProps<typeof DropdownMenuContent>;
    asContextMenu?: boolean;
    disabled?: boolean;
    onToggleMenu?: ToggleMenu;
    modal?: boolean;
    withoutShadow?: boolean;
    menuId?: string;
}

export const Menu = ({
    toggleButtonContent,
    isPortal = true,
    isForcedOpen,
    children,
    className,
    ToggleButton = MenuToggleButton,
    contentProps,
    asContextMenu,
    disabled,
    onToggleMenu,
    modal = true,
    withoutShadow,
    menuId,
}: MenuProps) => {
    const ref = useRef<HTMLButtonElement>(null);
    const theme = useTheme();
    const isMounted = useIsMounted();

    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = typeof isForcedOpen === 'boolean';
    const isOpen = isControlled ? isForcedOpen : internalOpen;

    const stopPropagation = useCallback((e: React.SyntheticEvent) => e.stopPropagation(), []);

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!isMounted.current) return;

            // Prevent Radix from opening the custom context menu automatically.
            const shouldIgnoreClosedContextMenuOpen = asContextMenu && !isOpen && nextOpen;

            if (disabled) {
                if (!nextOpen) {
                    setInternalOpen(false);
                }

                return;
            }

            if (shouldIgnoreClosedContextMenuOpen) {
                return;
            }

            if (!isControlled) {
                setInternalOpen(nextOpen);
            }

            onToggleMenu?.(nextOpen, ref.current ?? undefined);
        },
        [isMounted, disabled, isControlled, asContextMenu, isOpen, onToggleMenu],
    );

    // Handles only opening of the custom context menu.
    const handleContextMenu = useCallback(
        (e: React.MouseEvent) => {
            if (!isMounted.current) return;

            if (!asContextMenu || disabled) return;

            e.preventDefault();
            e.stopPropagation();

            if (!isControlled) {
                setInternalOpen(true);
            }

            onToggleMenu?.(true, ref.current ?? undefined);
        },
        [isMounted, asContextMenu, disabled, isControlled, onToggleMenu],
    );

    return (
        <span
            onClick={stopPropagation}
            onContextMenu={handleContextMenu}
        >
            <DropdownMenu
                open={isOpen}
                onOpenChange={handleOpenChange}
                modal={modal}
            >
                <ToggleButton ref={ref}>{toggleButtonContent}</ToggleButton>
                <DropdownMenuPrimitive.Portal container={isPortal ? undefined : ref.current?.parentElement}>
                    <DropdownMenuContent
                        id={menuId}
                        className={className}
                        theme={theme}
                        sideOffset={5}
                        align="end"
                        alignOffset={-10}
                        hideWhenDetached={true}
                        {...contentProps}
                        $withoutShadow={withoutShadow}
                    >
                        {children}
                    </DropdownMenuContent>
                </DropdownMenuPrimitive.Portal>
            </DropdownMenu>
        </span>
    );
};
